import { useMemo } from "react";
import { normalizeGradeLabel } from "@shared/grading";

interface PlacedItem {
  id: string;
  brand: string;
  modelName: string;
  grade: string;
  x: number;
  y: number;
  width: number;
  depth: number;
  rotated: boolean;
}

interface TruckLoadingVisualizationProps {
  placedItems: PlacedItem[];
  truckDimensions: {
    width: number;
    depth: number;
  };
}

const GRADE_COLORS: Record<string, string> = {
  'A': 'bg-green-500/80 border-green-600',
  'A+': 'bg-green-600/80 border-green-700',
  'A-': 'bg-blue-500/80 border-blue-600',
  'B': 'bg-yellow-500/80 border-yellow-600',
  'B+': 'bg-orange-500/80 border-orange-600',
  'B-': 'bg-yellow-400/80 border-yellow-500',
  'C': 'bg-yellow-500/80 border-yellow-600',
  'D': 'bg-orange-500/80 border-orange-600',
  'Scratch & Dent': 'bg-red-500/80 border-red-600',
};

export function TruckLoadingVisualization({ placedItems, truckDimensions }: TruckLoadingVisualizationProps) {
  // Calculate scale to fit visualization
  const scale = useMemo(() => {
    const maxWidth = 800; // max width in pixels
    const maxHeight = 400; // max height in pixels
    
    const scaleX = maxWidth / truckDimensions.width;
    const scaleY = maxHeight / truckDimensions.depth;
    
    return Math.min(scaleX, scaleY, 1); // Don't scale up, only down
  }, [truckDimensions]);

  const visualWidth = truckDimensions.width * scale;
  const visualDepth = truckDimensions.depth * scale;

  return (
    <div className="w-full" data-testid="truck-loading-visualization">
      <div className="mb-3 flex justify-between items-center text-sm text-muted-foreground">
        <span>Truck Bed Layout (Top View)</span>
        <span>{truckDimensions.width}" × {truckDimensions.depth}"</span>
      </div>
      
      <div className="border-2 border-foreground/20 rounded-lg p-4 bg-secondary/10 overflow-auto">
        <div 
          className="relative bg-background/50 border border-foreground/10 mx-auto"
          style={{ 
            width: `${visualWidth}px`, 
            height: `${visualDepth}px`,
          }}
        >
          {placedItems.map((item) => {
            const gradeColor = GRADE_COLORS[normalizeGradeLabel(item.grade)] || 'bg-gray-500/80 border-gray-600';
            const itemWidth = item.width * scale;
            const itemDepth = item.depth * scale;
            const itemX = item.x * scale;
            const itemY = item.y * scale;
            
            // Determine if we should show text based on item size
            const showBrand = itemWidth > 60 && itemDepth > 30;
            const showModel = itemWidth > 80 && itemDepth > 40;
            const showDimensions = itemWidth > 50 && itemDepth > 25;
            
            return (
              <div
                key={item.id}
                className={`absolute border-2 ${gradeColor} flex flex-col items-center justify-center text-white text-xs font-medium overflow-hidden transition-all hover:z-10 hover:shadow-lg`}
                style={{
                  left: `${itemX}px`,
                  top: `${itemY}px`,
                  width: `${itemWidth}px`,
                  height: `${itemDepth}px`,
                }}
                title={`${item.brand} ${item.modelName} (${normalizeGradeLabel(item.grade)})${item.rotated ? ' - Rotated' : ''}\n${item.width}" × ${item.depth}"`}
                data-testid={`placed-item-${item.id}`}
              >
                {showBrand && (
                  <div className="font-semibold truncate px-1 max-w-full">
                    {item.brand}
                  </div>
                )}
                {showModel && (
                  <div className="text-[10px] truncate px-1 max-w-full">
                    {item.modelName}
                  </div>
                )}
                <div className="font-bold text-sm">
                  {normalizeGradeLabel(item.grade)}
                </div>
                {showDimensions && (
                  <div className="text-[9px] opacity-80">
                    {item.width}" × {item.depth}"
                  </div>
                )}
                {item.rotated && (
                  <div className="text-[8px] opacity-70">↻</div>
                )}
              </div>
            );
          })}
        </div>
      </div>
      
      <div className="mt-3 flex flex-wrap gap-2 text-xs">
        <div className="flex items-center gap-1">
          <div className="w-4 h-4 bg-green-500/80 border border-green-600 rounded" />
          <span>A Grade</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-4 h-4 bg-blue-500/80 border border-blue-600 rounded" />
          <span>B Grade</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-4 h-4 bg-yellow-500/80 border border-yellow-600 rounded" />
          <span>C Grade</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-4 h-4 bg-orange-500/80 border border-orange-600 rounded" />
          <span>D Grade</span>
        </div>
      </div>
    </div>
  );
}
